import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Zap, Image as ImageIcon, ArrowRight, Video, TrendingUp, Play, BookOpen, GraduationCap } from 'lucide-react';
import axios from 'axios';
import Footer from './Footer';
import PresetPreviewMedia from './PresetPreviewMedia';
import './PublicLanding.css';
import './PresetsCarousel.css';

// Вспомогательная функция для определения типа медиа
const isVideoFile = (url) => {
  if (!url) return false;
  const urlLower = url.toLowerCase();
  if (urlLower.includes('mime_type=video')) return true;
  return urlLower.includes('.mp4') || urlLower.includes('.mov') || urlLower.includes('.webm') || urlLower.includes('.gif');
};

// Определяем embed URL (Vimeo / YouTube)
const isEmbedUrl = (url) => {
  if (!url) return false;
  const urlLower = url.toLowerCase();
  return urlLower.includes('player.vimeo.com') || urlLower.includes('youtube.com/embed') || urlLower.includes('youtu.be');
};

// Формируем URL с нужными параметрами для автовоспроизведения
const getEmbedUrl = (url) => {
  try {
    const u = new URL(url);
    u.searchParams.set('autoplay', '1');
    u.searchParams.set('muted', '1');
    u.searchParams.set('loop', '1');
    u.searchParams.set('background', '1'); // Vimeo: скрывает UI, тихий фон
    return u.toString();
  } catch {
    return url;
  }
};

function PublicLanding() {
  const navigate = useNavigate();
  const [modelsCount, setModelsCount] = useState(0);
  const [featuredModels, setFeaturedModels] = useState([]);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [presetsLoading, setPresetsLoading] = useState(true);
  
  // Получаем ref параметр из URL и сохраняем в sessionStorage
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode) {
      sessionStorage.setItem('referral_code', refCode);
    }
  }, []);

  // Функция для получения ref кода (из URL или sessionStorage)
  const getRefCode = () => {
    const params = new URLSearchParams(window.location.search);
    const refFromUrl = params.get('ref');
    return refFromUrl || sessionStorage.getItem('referral_code') || null;
  };

  useEffect(() => {
    fetchData();
    fetchPresets();
  }, []);

  const fetchData = async () => {
    try {
      const [modelsCountResponse, modelsResponse] = await Promise.all([
        axios.get('/api/ai-models/count', { params: { is_active: true } }),
        axios.get('/api/ai-models/landing', { params: { limit: 30 } })
      ]);
      const count = modelsCountResponse.data?.count || 0;
      const landingModels = modelsResponse.data || [];
      
      setModelsCount(count);
      setFeaturedModels(landingModels);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPresets = async () => {
    try {
      const presetsResponse = await axios.get('/api/presets/', {
        params: { is_active: true }
      });
      
      const loadedPresets = presetsResponse.data.map(preset => ({
        id: preset.preset_id,
        title: preset.title,
        previewImages: preset.preview_images || []
      }));
      
      setPresets(loadedPresets);
    } catch (error) {
      console.error('Error loading presets:', error);
    } finally {
      setPresetsLoading(false);
    }
  };

  const handlePresetClick = () => {
    // Для неавторизованных пользователей перенаправляем на регистрацию
    const refCode = getRefCode();
    navigate(refCode ? `/register?ref=${refCode}` : '/register');
  };

  const categories = {
    image_generation: 'Генерация',
    image_editing: 'Редактирование',
    enhancement: 'Улучшение',
    video_generation: 'Видео генерация',
    music_generation: 'Музыка',
    speech_generation: 'Речь',
    video_editing: 'Видео редактирование'
  };

  return (
    <div className="public-landing">
      {/* Header - Fixed on top */}
      <header className="public-header">
        <div className="public-logo">
          <img src="/logo.png" alt="Pikly" />
          <span>PIKLY</span>
        </div>
        <div className="public-actions">
          <button className="btn ghost" onClick={() => navigate('/courses')}>
            Обучение/курсы
          </button>
          <button className="btn ghost" onClick={() => {
            const refCode = getRefCode();
            navigate(refCode ? `/register?ref=${refCode}` : '/register');
          }}>Регистрация</button>
          <button className="btn primary" onClick={() => {
            const refCode = getRefCode();
            navigate(refCode ? `/login?ref=${refCode}` : '/login');
          }}>Вход</button>
        </div>
      </header>

      {/* Hero Section - Full Width */}
      <section className="hero-section-full">
        <div className="hero-background">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
        </div>
        
        <div className="hero-content">
          <div className="hero-badge">
            <Sparkles size={16} />
            <span>AI‑платформа для креаторов</span>
          </div>
          <h1 className="hero-title">
            От креаторов для креаторов!
            <span className="gradient-text"> вместе с PIKLY</span>
          </h1>
          <p className="hero-subtitle">
            Генерация изображений и видео с помощью передовых AI моделей. 
            От быстрых набросков до профессионального контента в 4K качестве.
          </p>
          <div className="cta">
            <button className="btn primary lg" onClick={() => navigate('/register')}>
              Начни прямо сейчас
              <ArrowRight size={18} />
            </button>
            <button className="btn ghost lg" onClick={() => navigate('/login')}>
              Войти
            </button>
          </div>

          <div className="hero-stats">
            <div className="stat-box">
              <div className="stat-icon">
                <ImageIcon size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-value">{loading ? '—' : (modelsCount || '—')}</div>
                <div className="stat-label">AI моделей</div>
              </div>
            </div>
            <div className="stat-box">
              <div className="stat-icon">
                <Zap size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-value">~10с</div>
                <div className="stat-label">Скорость</div>
              </div>
            </div>
            <div className="stat-box">
              <div className="stat-icon">
                <TrendingUp size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-value">4K</div>
                <div className="stat-label">Качество</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Effects Section */}
      {presets.length > 0 && (
        <section className="effects-section-public">
          <div className="effects-container-public">
            <div className="section-header-public">
              <h2 className="section-title">Эффекты</h2>
              <p className="section-subtitle">Посмотрите примеры работ, созданных с помощью наших AI моделей</p>
            </div>
            {presetsLoading ? (
              <div className="presets-grid-loading">
                <div className="loading-spinner"></div>
              </div>
            ) : (
              <div className="presets-grid">
                {presets.slice(0, 12).map((preset) => (
                  <div 
                    key={preset.id} 
                    className="preset-card"
                    onClick={handlePresetClick}
                  >
                    <div className="preset-preview">
                      <div className={`preset-images ${(preset.previewImages || []).length === 1 ? 'single-media' : ''}`}>
                        {(preset.previewImages || []).slice(0, 2).map((mediaUrl, idx) => (
                          <PresetPreviewMedia
                            key={idx}
                            src={mediaUrl}
                            title={preset.title}
                            index={idx}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="preset-info">
                      <h3>{preset.title}</h3>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="effects-cta">
              <button className="btn primary lg" onClick={() => navigate('/register')}>
                Создать свой эффект
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Promo Models Section */}
      {featuredModels.length > 0 && (
        <section className="promo-models-section">
          <div className="promo-container">
            <h2 className="section-title">Популярные модели</h2>
            <div className="promo-models-grid">
              {featuredModels.map((model, index) => {
                const isVideo = model.cover_image_url && isVideoFile(model.cover_image_url);
                const isEmbed = model.cover_image_url && isEmbedUrl(model.cover_image_url);
                return (
                  <div
                    key={model.id || `model-${index}`}
                    className="promo-model-card"
                    onClick={() => navigate('/register')}
                  >
                    <div className="promo-model-image">
                      {model.cover_image_url ? (
                        isEmbed ? (
                          <iframe
                            src={getEmbedUrl(model.cover_image_url)}
                            frameBorder="0"
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                            title={model.display_name}
                            style={{ width: '100%', height: '100%', pointerEvents: 'none', border: 'none' }}
                          />
                        ) : isVideo ? (
                          <video 
                            src={model.cover_image_url} 
                            muted
                            loop
                            playsInline
                            autoPlay={true}
                            preload="auto"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const placeholder = e.target.nextElementSibling;
                              if (placeholder) placeholder.style.display = 'flex';
                            }}
                            onLoadedData={(e) => {
                              e.target.play().catch(err => {
                                console.log('Autoplay prevented:', err);
                              });
                            }}
                          />
                        ) : (
                          <img 
                            src={model.cover_image_url} 
                            alt={model.display_name}
                            loading="lazy"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const placeholder = e.target.nextElementSibling;
                              if (placeholder) placeholder.style.display = 'flex';
                            }}
                          />
                        )
                      ) : (
                        <div className="promo-placeholder">
                          <Zap size={48} />
                        </div>
                      )}
                      {!model.cover_image_url && (
                        <div className="promo-placeholder">
                          <Zap size={48} />
                        </div>
                      )}
                      {(isVideo || isEmbed) && model.cover_image_url && (
                        <div className="promo-video-indicator">
                          <Play size={16} fill="white" />
                        </div>
                      )}
                    </div>
                    <div className="promo-model-info">
                      <h3 className="promo-model-name">{model.display_name}</h3>
                      <p className="promo-model-description">
                        {model.short_description || model.description || 'Мощная AI модель для создания контента'}
                      </p>
                      <div className="promo-model-footer">
                        <div className="promo-model-category">
                          {categories[model.category] || model.category}
                        </div>
                        {model.estimated_time_seconds && (
                          <div className="promo-model-time">
                            ~{model.estimated_time_seconds}с
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="features-section">
        <div className="features-container">
          <h2 className="section-title">Возможности платформы</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <ImageIcon size={32} />
              </div>
              <h3>Генерация изображений</h3>
              <p>Создавайте уникальные изображения по текстовому описанию с помощью передовых AI моделей</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <Video size={32} />
              </div>
              <h3>Генерация видео</h3>
              <p>Создавайте динамичные видео из изображений или текстовых промптов</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <Sparkles size={32} />
              </div>
              <h3>Редактирование</h3>
              <p>Улучшайте, восстанавливайте и стилизуйте существующие изображения</p>
            </div>
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section className="blog-section">
        <div className="blog-container">
          <div className="blog-header">
            <GraduationCap size={32} />
            <h2 className="section-title">Обучение</h2>
            <p className="section-subtitle">
              Курсы по нейросетям — подайте заявку без регистрации, мы свяжемся с вами
            </p>
          </div>
          <button
            className="btn primary lg"
            onClick={() => navigate('/courses/form')}
          >
            Оставить заявку
            <ArrowRight size={18} />
          </button>
        </div>
      </section>

      {/* Blog Section */}
      <section className="blog-section">
        <div className="blog-container">
          <div className="blog-header">
            <BookOpen size={32} />
            <h2 className="section-title">Блог</h2>
            <p className="section-subtitle">Узнайте о последних новостях и разработках в мире AI</p>
          </div>
          <button 
            className="btn primary lg"
            onClick={() => navigate('/blog')}
          >
            Читать статьи
            <ArrowRight size={18} />
          </button>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-container">
          <h2 className="cta-title">Готовы начать?</h2>
          <p className="cta-subtitle">Присоединяйтесь к тысячам креаторов, создающих контент с помощью AI</p>
          <button className="btn primary xl" onClick={() => navigate('/register')}>
            Начни прямо сейчас
            <ArrowRight size={20} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default PublicLanding;


